/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PlanService } from 'mobile/app/services/plan/plan.service';
import { SideNavService } from 'mobile/app/services/side-nav/side-nav.service';
import { TicketService } from 'mobile/app/services/ticket/ticket.service';
import { Subject, Observable, combineLatest } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { GroupedTicket } from '@touchplan/ticket';
import { ProjectMember, Role, TicketFields, Plan } from '@touchplan/common';
import { TicketContext } from 'src/app/modules/ticket-edit-panel/ticket-context';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { BannerMessageHandler } from 'src/app/common/logging/banner-message-handler';
import { PLAN_ACCESS_ON_LOAD } from '@touchplan/strings';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';

@Component({
	selector: 'ticket-details',
	templateUrl: './ticket-details.component.html',
	styleUrls: ['./ticket-details.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketDetailsComponent implements OnInit, OnDestroy {
	public planId: string;
	public projectId: string;
	public ticket$: Observable<GroupedTicket>;
	public plan$: Observable<Plan>;
	public roleList$: Observable<Role[]>;
	public locationList$: Observable<any[]>;
	public projectMembers$: Observable<ProjectMember[]>;
	public ticketContext: TicketContext;
	private shutdownSubject$: Subject<boolean> = new Subject();
	private oldTitle: string;

	constructor(
		public ticketService: TicketService,
		public planService: PlanService,
		private sideNavService: SideNavService,
		private route: ActivatedRoute,
		public featureFlagManager: FeatureFlagManager,
		private router: Router,
		private bannerMessageHandler: BannerMessageHandler
	) {
		// some duplicate construction debugging https://mocasystems.atlassian.net/browse/TP-3265
		// if (window.TicketDetailsComponentDebug) {
		// 	console.error('it done broke');
		// }
		// window.TicketDetailsComponentDebug = true;
		console.log('TicketDetailsComponent construct');
	}

	public ngOnInit(): void {
		this.roleList$ = this.ticketService.roles$;
		this.locationList$ = this.ticketService.locations$;
		this.projectMembers$ = this.ticketService.projectMembers$;

		this.ticketContext = new TicketContext();
		this.sideNavService.setDisplayFilterIcon(false);
		this.sideNavService.setDisplayBackArrowIcon(true);
		this.sideNavService.setDisplayScrollToTodayIcon(false);
		this.ticket$ = this.ticketService.selectedTicket$;

		this.oldTitle = this.sideNavService.getPageTitle$().value;

		this.ticket$
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((t) => this.sideNavService.setPageTitle(t?.view?.activityName));

		this.plan$ = this.ticketService.plan$.asObservable();

		//there's a... 2 tier initializion going on here, first we init some ticket stuff directly in the ngOnInit
		// but then this. It's... kind of weird?
		this.route.parent?.params?.pipe(takeUntil(this.shutdownSubject$)).subscribe((params) => {
			this.planId = params.planId;
			this.projectId = params.projectId;

			this.ticketContext.projectId = this.projectId;
			this.ticketContext.planId = this.planId;
			this.ticketContext.userId = this.route.parent.snapshot.data.userData.$id;
			this.ticketContext.openInLightBox = true;
			this.ticketContext.fixedDimensionsUI = true;
			this.ticketContext.initTicketFields(this.ticket$, this.ticketService.planAccessCheck);
		});
		this.checkForPlanAccessOnLoad();
		this.viewedATicket();
	}

	public ticketDataChanged(event: { ticket: GroupedTicket; updateData: TicketFields }) {
		console.log('ticket data changed', event.ticket.$id, event.updateData);
		this.ticketService.applyLiveUpdate(event.ticket.$id, event.updateData);
	}

	public persistTicket(ticket: GroupedTicket) {
		this.ticketService.persistTicketData(ticket.$id);
		console.log('persistTicket', ticket);
	}

	public resetTicketToDatabase(ticket: GroupedTicket) {
		console.log('resetTicketToDatabase', ticket);
		this.ticketService.resetTicketEditDataToDatabase(ticket.$id);
	}

	public goToTicketList(ticket?: GroupedTicket) {
		//TODO: call some sort of standard "go back" code. This currently appears to be isolated in
		//projects/mobile/src/app/common-components/main-header/main-header.component.ts

		console.log('docked ticket wants to close');
		//apparently this works here but not in main-header.component.ts?
		this.router.navigate(['../'], { relativeTo: this.route });
	}

	protected viewedATicket() {
		combineLatest([this.ticket$, this.ticketService.planAccessCheck.hasRestrictedAccess$])
			.pipe(take(1), takeUntil(this.shutdownSubject$))
			.subscribe(([ticket, hasRestrictedAccess]) => {
				// Set the last param (isFloating) to a hardcoded false since in mobile, users should not see any floating tickets in the list.
				// If this changes in the future, we need to update this.
				analyticsEventsService.viewedATicket(ticket.$id, this.planId, hasRestrictedAccess, false);
			});
	}

	public ngOnDestroy(): void {
		this.ticketService.unselectTicket();
		this.sideNavService.setPageTitle(this.oldTitle);
		this.sideNavService.setDisplayFilterIcon(true);
		this.sideNavService.setDisplayScrollToTodayIcon(true);
		this.shutdownSubject$.next(true);
	}

	private checkForPlanAccessOnLoad() {
		this.ticketService
			.allowPlanAccessOnLoad$()
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe((allowAccess) => {
				if (!allowAccess) {
					this.bannerMessageHandler.displayErrorMessage(new Error(PLAN_ACCESS_ON_LOAD));
					this.router.navigate(['/projects', this.projectId]);
				}
			});
	}
}
